import { useEffect } from "react";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardHeader,
  Chip,
  InputBase,
  Paper,
  Skeleton,
  Stack,
  Typography,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import useSWR from "swr";
import { useOutletContext, useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import TagIcon from "@mui/icons-material/Tag";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import InternalLink from "../../../components/Reusable/InternalLink";

const ALLOWED_TYPES = new Set(["accepted", "intake"]);

// Wraps the first case-insensitive occurrence of `query` in `text` with a
// highlight so the user can see why a row matched their search.
function Highlight({ text, query }) {
  const value = text ?? "";
  if (!query) return value;
  const idx = value.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return value;
  return (
    <>
      {value.slice(0, idx)}
      <Box
        component="mark"
        sx={{
          px: "2px",
          borderRadius: "3px",
          bgcolor: (theme) =>
            theme.functions.rgba(theme.palette.secondary.main, 0.45),
          color: "inherit",
        }}
      >
        {value.slice(idx, idx + query.length)}
      </Box>
      {value.slice(idx + query.length)}
    </>
  );
}

function PatientListWithFilter() {
  const { api } = useOutletContext();
  const [params, setParams] = useSearchParams();

  const swrKey = "/patients";
  const { isLoading, error, data } = useSWR(swrKey, () => api.getPatients());

  const typeParam = params.get("type");
  const tab = ALLOWED_TYPES.has(typeParam) ? typeParam : "accepted";

  // Search lives in the URL (?q=...) so it survives back-navigation, exactly
  // like the tab does. replace:true keeps each keystroke out of history.
  const search = params.get("q") ?? "";

  useEffect(() => {
    if (typeParam !== tab) {
      params.set("type", tab);
      setParams(params, { replace: true });
    }
  }, [tab, typeParam, params, setParams]);

  const onTabChange = (_, tabVal) => {
    if (tabVal === null) return;
    params.set("type", tabVal);
    setParams(params, { replace: true });
  };

  const onSearchChange = (event) => {
    const val = event.target.value;
    if (val) params.set("q", val);
    else params.delete("q");
    setParams(params, { replace: true });
  };

  const clearSearch = () => {
    params.delete("q");
    setParams(params, { replace: true });
  };

  if (isLoading || !data) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={40} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  const intakes = data.filter((v) => v.rec_type === "intake");
  const patients = data.filter((v) => v.rec_type === "accepted");

  const query = search.toLowerCase().trim();
  const pats = (
    tab === "intake" ? intakes : tab === "accepted" ? patients : []
  ).filter((v) => {
    if (query === "") return true;
    return (
      v.name_first.toLowerCase().startsWith(query) ||
      v.name_last.toLowerCase().startsWith(query) ||
      v.addr_city.toLowerCase().startsWith(query) ||
      String(v.id).toLowerCase().includes(query)
    );
  });

  return (
    <>
      <ToggleButtonGroup
        sx={{ mb: 2 }}
        color="primary"
        value={tab}
        exclusive
        onChange={onTabChange}
        fullWidth
      >
        <ToggleButton value="intake">Intake ({intakes.length})</ToggleButton>
        <ToggleButton value="accepted">
          Accepted ({patients.length})
        </ToggleButton>
      </ToggleButtonGroup>
      <Paper
        sx={{ p: "2px 4px", display: "flex", alignItems: "center", mb: 2 }}
      >
        <SearchIcon color="action" sx={{ ml: 1 }} />
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Filter by name, city, or ID"
          onChange={onSearchChange}
          value={search}
          inputProps={{ "aria-label": "filter patients" }}
        />
        {search !== "" && (
          <IconButton
            size="small"
            aria-label="clear search"
            onClick={clearSearch}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>

      {pats.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, ml: 0.5 }}
        >
          {pats.length} {pats.length === 1 ? "patient" : "patients"}
          {query !== ""
            ? pats.length === 1
              ? " matches your filter"
              : " match your filter"
            : ""}
        </Typography>
      )}

      {pats.length === 0 && (
        <Paper sx={{ py: 3, px: 2 }} elevation={0}>
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            {query === ""
              ? "No patients found."
              : `No ${tab} patients match "${search}".`}
          </Typography>
        </Paper>
      )}

      {pats.length > 0 && (
        <Stack spacing={1} sx={{ maxWidth: "md", margin: "auto" }}>
          {pats.map((p) => {
            return (
              <Card key={p.id}>
                <CardActionArea
                  component={InternalLink}
                  to={
                    tab === "intake" ? `/intake/${p.id}` : `/patients/${p.id}`
                  }
                >
                  <CardHeader
                    subheaderTypographyProps={{ component: "div" }}
                    title={
                      <Typography variant="h5">
                        <Highlight text={p.name_last} query={query} />,{" "}
                        <Highlight text={p.name_first} query={query} />
                      </Typography>
                    }
                    subheader={
                      <Stack spacing={0.75} sx={{ mt: 0.25 }}>
                        <Typography variant="subtitle2">
                          <Highlight text={p.addr_city} query={query} />,{" "}
                          {p.addr_state}
                        </Typography>
                        <Chip
                          size="small"
                          icon={<TagIcon />}
                          label={`ID ${p.id}`}
                          sx={{ alignSelf: "flex-start", fontWeight: 500 }}
                        />
                      </Stack>
                    }
                    action={
                      <IconButton size="small">
                        <ArrowForwardIosIcon />
                      </IconButton>
                    }
                  />
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}
    </>
  );
}

export default PatientListWithFilter;
